import React from 'react';
import { SynthesisMetadata } from '../../models/inspectionEvaluationIssue';
import { 
  AlertTriangle, 
  Check, 
  Clock, 
  FileText, 
  AlertCircle,
  TextSelect
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

interface InspectionProcessingMetadataProps {
  metadata: SynthesisMetadata;
  className?: string;
}

const InspectionProcessingMetadata: React.FC<InspectionProcessingMetadataProps> = ({
  metadata,
  className = ''
}) => {
  if (!metadata) return null;

  const {
    totalPages,
    completedPages,
    issuePages,
    prioritizedIssuePages,
    processingSuccess,
    processingError,
    textBasedAnalysis,
    fallbackUsed,
    criticalError
  } = metadata;

  // Calculate completion percentage
  const completionPercentage = totalPages > 0 
    ? Math.round((completedPages / totalPages) * 100) 
    : 0;

  // Determine alert status
  const getStatusColor = () => {
    if (criticalError) return 'bg-red-100 text-red-800 border-red-200';
    if (!processingSuccess) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (fallbackUsed) return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-green-100 text-green-800 border-green-200';
  };

  // Determine status message
  const getStatusMessage = () => {
    if (criticalError) return 'Critical Error';
    if (!processingSuccess) return processingError || 'Processing Error';
    if (fallbackUsed) return 'Fallback Used';
    return 'Processing Complete';
  };

  return (
    <div className={`rounded-lg border border-gray-200 p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Processing Information</h3>
        <Badge className={getStatusColor()}>
          {processingSuccess ? <Check className="h-3 w-3 mr-1" /> : <AlertTriangle className="h-3 w-3 mr-1" />}
          {getStatusMessage()}
        </Badge>
      </div>

      {/* Progress bar for page completion */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-500">Page Processing</span>
          <span className="text-xs font-medium">{completedPages} of {totalPages} pages ({completionPercentage}%)</span>
        </div>
        <Progress value={completionPercentage} className="h-2" />
      </div>

      {/* Processing details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <div className="flex items-center text-xs text-gray-600">
          <FileText className="h-3.5 w-3.5 mr-2 text-gray-500" />
          {issuePages} {issuePages === 1 ? 'page' : 'pages'} with issues identified
        </div>
        
        {prioritizedIssuePages && (
          <div className="flex items-center text-xs text-gray-600">
            <AlertCircle className="h-3.5 w-3.5 mr-2 text-amber-500" />
            Priority analysis on issue pages
          </div>
        )}
        
        {textBasedAnalysis && (
          <div className="flex items-center text-xs text-gray-600">
            <TextSelect className="h-3.5 w-3.5 mr-2 text-gray-500" />
            Text-based analysis performed
          </div>
        )}
      </div>

      {/* Error message if present */}
      {!processingSuccess && processingError && (
        <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-700">
          {processingError}
        </div>
      )}
    </div>
  );
};

export default InspectionProcessingMetadata;